import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useQuery } from "@apollo/react-hooks";
import { useMeta } from "@/hooks";
import { QUERY_LIST_STORE_ORDER } from "../../../apollo/module-operations/order";
import { orderListNodeToAlgoliaOrder } from "./orderListNodeToAlgoliaOrder";
import type { AlgoliaOrder } from "@/types";

const DEFAULT_PAGE_SIZE_TABLE = 25;

/** Apollo networkStatus for `fetchMore` (pagination), not a new primary query. */
const NETWORK_STATUS_FETCH_MORE = 3;

export interface OrdersFilterState {
    created_at_start?: number;
    created_at_end?: number;
    status: string[];
    type: string[];
    total_price_min?: number;
    total_price_max?: number;
}

const getDefaultFilterState = (): OrdersFilterState => {
    const now = new Date();
    const todayStart = Math.floor(
        new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() /
            1000,
    );
    return {
        status: ["APPROVED"],
        type: [],
        created_at_start: todayStart,
        created_at_end: undefined,
    };
};

interface OrdersDataState {
    orders: AlgoliaOrder[];
    totalCount: number;
    loading: boolean;
    refetch: () => void;
    fetchMore: () => void;
    hasNextPage: boolean;
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    filterState: OrdersFilterState;
    setFilterState: React.Dispatch<React.SetStateAction<OrdersFilterState>>;
    /** Current GraphQL / client page size (table footer + fetch chunk). */
    pageSize: number;
    setPageSize: (size: number) => void;
}

const OrdersDataContext = createContext<OrdersDataState | undefined>(undefined);

interface Edge {
    cursor?: string;
    node?: Record<string, unknown>;
}

interface ListStoreOrders {
    totalCount?: number | null;
    edges?: Edge[] | null;
    pageInfo?: { hasPreviousPage?: boolean | null } | null;
}

const getOrderId = (order: AlgoliaOrder): string | undefined => {
    // AlgoliaOrder comes from the GraphQL node; we assume it has at least an entity_id or key.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyOrder: any = order;
    return anyOrder.entity_id ?? anyOrder.key ?? undefined;
};

const buildFilterQuery = (
    searchQuery: string,
    filterState: OrdersFilterState,
): string => {
    const parts: string[] = [];
    const trimmed = (searchQuery || "").trim();
    if (trimmed) {
        parts.push(`(order_number == '${trimmed}' || email == '${trimmed}')`);
    }
    if (filterState.created_at_start != null) {
        parts.push(`created_at >= ${filterState.created_at_start}`);
    }
    if (filterState.created_at_end != null) {
        parts.push(`created_at <= ${filterState.created_at_end}`);
    }
    if (Array.isArray(filterState.status) && filterState.status.length > 0) {
        parts.push(
            `(${filterState.status.map(s => `status == '${s}'`).join(" || ")})`,
        );
    }
    if (Array.isArray(filterState.type) && filterState.type.length > 0) {
        parts.push(
            `(${filterState.type.map(t => `type == '${t}'`).join(" || ")})`,
        );
    }
    if (filterState.total_price_min != null) {
        parts.push(`total_price >= ${filterState.total_price_min}`);
    }
    if (filterState.total_price_max != null) {
        parts.push(`total_price <= ${filterState.total_price_max}`);
    }
    return parts.length > 0 ? parts.join(" && ") : "true";
};

export function OrdersDataProvider({
    children,
    pageSize: pageSizeProp = DEFAULT_PAGE_SIZE_TABLE,
}: {
    children: React.ReactNode;
    pageSize?: number;
}) {
    const { user } = useMeta();
    const [searchQuery, setSearchQueryState] = useState("");
    const [filterState, setFilterState] = useState<OrdersFilterState>(
        getDefaultFilterState(),
    );
    const [pageSize, setPageSizeState] = useState(pageSizeProp);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    /* Kanban ↔ table toggle (and other parents) may pass a new default size. */
    useEffect(() => {
        setPageSizeState(pageSizeProp);
    }, [pageSizeProp]);

    const setPageSize = useCallback((size: number) => {
        setPageSizeState(size);
    }, []);

    const queryStr = useMemo(
        () => buildFilterQuery(searchQuery, filterState),
        [searchQuery, filterState],
    );

    const skip = Boolean(user?.loading || !user?.accountId || !user?.storeId);

    const {
        loading,
        data,
        networkStatus,
        refetch: apolloRefetch,
        fetchMore: apolloFetchMore,
    } = useQuery(QUERY_LIST_STORE_ORDER, {
        skip,
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
        variables: {
            account_id: user?.accountId,
            store_id: user?.storeId,
            query: queryStr,
            last: pageSize,
        },
    });

    /**
     * Filter / page-size changes re-run the primary query. Reporting the
     * previous (stale) page while it resolves is what made orders show up and
     * then vanish, so results are only exposed once the current query settles.
     */
    const isPrimaryLoading =
        skip || (loading && networkStatus !== NETWORK_STATUS_FETCH_MORE);

    const list: ListStoreOrders | undefined = data?.listStoreOrders;

    const orders = useMemo<AlgoliaOrder[]>(() => {
        if (isPrimaryLoading) return [];
        const accountId = user?.accountId ?? "";
        const storeId = user?.storeId ?? "";
        const byId = new Map<string, AlgoliaOrder>();
        (list?.edges || []).forEach(edge => {
            const order = orderListNodeToAlgoliaOrder(
                edge?.node,
                accountId,
                storeId,
            );
            const id = getOrderId(order);
            if (id) byId.set(String(id), order);
        });
        return Array.from(byId.values());
    }, [isPrimaryLoading, list, user?.accountId, user?.storeId]);

    const totalCount = isPrimaryLoading ? 0 : list?.totalCount ?? 0;
    const hasNextPage = isPrimaryLoading
        ? false
        : Boolean(list?.pageInfo?.hasPreviousPage);

    const refetch = useCallback(() => {
        if (skip) return;
        apolloRefetch();
    }, [apolloRefetch, skip]);

    const setSearchQuery = useCallback((q: string) => {
        setSearchQueryState(q);
    }, []);

    const fetchMore = useCallback(() => {
        if (skip || isFetchingMore || isPrimaryLoading) return;
        if (orders.length >= totalCount) return;

        const edges = list?.edges || [];
        const lastCursor = edges[edges.length - 1]?.cursor;
        if (!lastCursor) return;

        setIsFetchingMore(true);
        apolloFetchMore({
            variables: {
                account_id: user?.accountId,
                store_id: user?.storeId,
                query: queryStr,
                last: pageSize,
                before: lastCursor,
            },
            updateQuery: (
                prev: { listStoreOrders?: ListStoreOrders },
                {
                    fetchMoreResult,
                }: {
                    fetchMoreResult?: { listStoreOrders?: ListStoreOrders };
                },
            ) => {
                if (!fetchMoreResult?.listStoreOrders) return prev;
                return {
                    listStoreOrders: {
                        ...prev.listStoreOrders,
                        ...fetchMoreResult.listStoreOrders,
                        edges: [
                            ...(prev.listStoreOrders?.edges || []),
                            ...(fetchMoreResult.listStoreOrders.edges || []),
                        ],
                    },
                };
            },
        })
            .catch(() => {
                // noop: errors are handled by Apollo, but we release the flag
            })
            .finally(() => {
                setIsFetchingMore(false);
            });
    }, [
        apolloFetchMore,
        isFetchingMore,
        isPrimaryLoading,
        list,
        orders.length,
        pageSize,
        queryStr,
        skip,
        totalCount,
        user?.accountId,
        user?.storeId,
    ]);

    const value: OrdersDataState = {
        orders,
        totalCount,
        loading: isPrimaryLoading,
        refetch,
        fetchMore,
        hasNextPage,
        searchQuery,
        setSearchQuery,
        filterState,
        setFilterState,
        pageSize,
        setPageSize,
    };

    return (
        <OrdersDataContext.Provider value={value}>
            {children}
        </OrdersDataContext.Provider>
    );
}

export function useOrdersData(): OrdersDataState {
    const ctx = useContext(OrdersDataContext);
    if (ctx === undefined) {
        throw new Error("useOrdersData must be used within OrdersDataProvider");
    }
    return ctx;
}
